/* Student progress.

   Course content carries structure and order, not student state — where someone
   stands is per student and belongs in the browser, so it is stored separately
   and layered over the content at render time (see lib/gating.js).

   localStorage is the right store for now: no accounts, no backend, and Canvas
   remains the system of record for anything that counts toward a grade. This is
   "where was I", not a gradebook.
*/

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { PROGRESS_ENABLED } from '../config.js';

export { applyProgress } from './gating.js';

const KEY = 'js1.progress';

const read = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; }
};

const ProgressContext = createContext(null);

export function ProgressProvider({ children }) {
  const [done, setDone] = useState(() => (PROGRESS_ENABLED ? read() : {}));

  /* With the flag off nothing is written — not "written and ignored". A store
     that silently keeps per-device state nobody can see or carry is a promise
     the site cannot keep, so it makes none. `done` stays empty, every consumer
     already handles that, and turning the flag on starts from a clean slate. */
  const persist = useCallback(next => {
    if (!PROGRESS_ENABLED) return;
    setDone(next);
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* session only */ }
  }, []);

  const complete = useCallback(id => persist({ ...read(), [id]: Date.now() }), [persist]);
  const uncomplete = useCallback(id => {
    const next = { ...read() };
    delete next[id];
    persist(next);
  }, [persist]);
  const reset = useCallback(() => persist({}), [persist]);

  useEffect(() => {
    if (!PROGRESS_ENABLED) return undefined;
    const on = e => { if (e.key === KEY) setDone(read()); };
    window.addEventListener('storage', on);
    return () => window.removeEventListener('storage', on);
  }, []);

  const value = useMemo(
    () => ({ done, complete, uncomplete, reset, enabled: PROGRESS_ENABLED }),
    [done, complete, uncomplete, reset]);
  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
}

export function useProgress() {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error('useProgress must be used inside <ProgressProvider>');
  return ctx;
}
